import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  CloudDownload,
  FileCode2,
  FolderOpen,
  Loader2,
  Upload,
  Wand2,
} from "lucide-react";
import {
  fetchCognosSpec,
  parseReport,
  parseReportUpload,
  type CognosReportIR,
  type CognosSpec,
} from "@/lib/api";
import { setLastIr } from "@/lib/workflow";
import { formatXml, validateXml } from "@/lib/xmlFormat";
import { ContentPicker } from "@/components/ContentPicker";

/**
 * Asset platforms Bridge can parse from. Currently only Cognos has a
 * parser ({@code CognosReportXmlParser}); the other entries are
 * disabled until parsers ship for them. Modeled as a list rather than
 * a hardcoded {@code <option>} so adding a parser later only touches
 * this constant.
 */
const SOURCE_PLATFORMS: ReadonlyArray<{
  value: string;
  label: string;
  enabled: boolean;
}> = [
  { value: "cognos", label: "IBM Cognos", enabled: true },
  { value: "powerbi", label: "Power BI (coming soon)", enabled: false },
  { value: "tableau", label: "Tableau (coming soon)", enabled: false },
  { value: "qlik", label: "Qlik Sense (coming soon)", enabled: false },
];

export function ParsePage() {
  const navigate = useNavigate();
  const [platform, setPlatform] = useState("cognos");
  const [assetId, setAssetId] = useState("rpt-1");
  const [xml, setXml] = useState(SAMPLE_XML);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loadFeedback, setLoadFeedback] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const mutation = useMutation<CognosReportIR, Error>({
    mutationFn: () => parseReport({ platform, value: assetId }, xml),
    onSuccess: (ir) => setLastIr(ir),
  });

  const uploadMutation = useMutation<CognosReportIR, Error, File>({
    mutationFn: (file) => parseReportUpload(file, platform, assetId || undefined),
    onSuccess: (ir) => setLastIr(ir),
  });

  // Fetch a Cognos report's XML by search-path, drop it into the
  // textarea, and flash a small "Loaded N chars" status. Used both
  // from the manual "Load XML" button and as an auto-trigger after
  // the picker confirms a leaf object.
  const loadSpec = useMutation<CognosSpec, Error, string>({
    mutationFn: (searchPath) => fetchCognosSpec(searchPath),
    onSuccess: (data) => {
      if (data.found && data.xml) {
        setXml(data.xml);
        setLoadFeedback(
          `Loaded ${data.sizeBytes?.toLocaleString() ?? data.xml.length} chars` +
            (data.contentType ? ` (${data.contentType})` : ""),
        );
      } else {
        setLoadFeedback(data.message ?? "Cognos returned no spec for this path.");
      }
    },
    onError: (e) => setLoadFeedback(`Load failed — ${e.message}`),
  });

  // Live XML validation — runs against the raw textarea on every keystroke.
  // Cheap (a single DOMParser pass on a few-KB document); we don't debounce
  // because the user feels the badge updating as they type.
  const xmlValidation = useMemo(() => validateXml(xml), [xml]);

  function beautify() {
    // Don't reformat on invalid input — that would garble the user's
    // partial paste. We surface the error in the badge instead.
    if (!xmlValidation.ok) return;
    const pretty = formatXml(xml);
    if (pretty && pretty !== xml) setXml(pretty);
  }

  function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!assetId.trim()) {
      const fallback = f.name.replace(/\.[^.]+$/, "");
      if (fallback) setAssetId(fallback);
    }
    uploadMutation.mutate(f);
    // reset so picking the same file again re-fires onChange
    e.target.value = "";
  }

  // Cognos search-paths look like /content/folder[@name='X']/report[@name='Y'];
  // anything starting with `/` is a candidate for the spec endpoint.
  // Free-text "rpt-1" placeholders can't be resolved — disable the
  // button rather than triggering a doomed SOAP call.
  const canFetchFromCognos =
    platform === "cognos" && assetId.trim().startsWith("/");

  function onPickerConfirm(picked: { path: string; name: string; objectClass: string }) {
    setAssetId(picked.path);
    setPickerOpen(false);
    setLoadFeedback(null);
    // Auto-trigger the spec fetch when the picked entry is a
    // spec-bearing class. The exporter handles report / query /
    // analysis / package / module / dataModule — folders /
    // namespaces / dataSources don't have specs and would 404.
    const klass = picked.objectClass.toLowerCase();
    const specClasses = new Set([
      "report", "query", "analysis", "interactivereport",
      "_package", "package", "model",
      "module", "dataModule", "datamodule", "basemodule",
    ]);
    if (specClasses.has(klass) && platform === "cognos") {
      loadSpec.mutate(picked.path);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display text-2xl font-semibold text-bridge-ink">
          Parse a Cognos report
        </h1>
        <p className="mt-0.5 text-sm text-bridge-slate">
          Paste the report XML; Bridge produces a normalized intermediate
          representation that the translators consume.
        </p>
      </header>

      <section data-tour-id="parse-upload" className="grid gap-3 rounded-xl2 border border-bridge-slate/20 bg-white p-4 shadow-card">
        <div className="grid grid-cols-[1fr,2fr] gap-3">
          <Field label="Asset platform">
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="w-full rounded-md border border-bridge-slate/30 bg-white px-2 py-1 text-sm focus:border-bridge-primary focus:outline-none focus:ring-2 focus:ring-bridge-primary/20"
              aria-label="Source platform of the asset being parsed"
            >
              {SOURCE_PLATFORMS.map((p) => (
                <option key={p.value} value={p.value} disabled={!p.enabled}>
                  {p.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Asset id">
            <input
              value={assetId}
              onChange={(e) => setAssetId(e.target.value)}
              className="w-full rounded-md border border-bridge-slate/30 bg-white px-2 py-1 text-sm"
              placeholder="/content/folder[@name='Sales']/report[@name='Q3']"
            />
          </Field>
        </div>
        <Field label="Cognos report XML">
          <div className="overflow-hidden rounded-md border border-bridge-slate/30 bg-white">
            <div className="flex flex-wrap items-center gap-2 border-b border-bridge-slate/20 bg-bridge-mist/40 px-2 py-1">
              <XmlStatusBadge validation={xmlValidation} />
              <button
                type="button"
                onClick={beautify}
                disabled={!xmlValidation.ok}
                className="ml-auto inline-flex items-center gap-1 rounded-md border border-bridge-slate/30 bg-white px-2 py-0.5 text-2xs font-medium text-bridge-ink hover:bg-bridge-mist disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  xmlValidation.ok
                    ? "Pretty-print the XML in place"
                    : "Fix the XML errors above before formatting"
                }
              >
                <Wand2 size={11} /> Format
              </button>
            </div>
            <textarea
              value={xml}
              onChange={(e) => setXml(e.target.value)}
              rows={14}
              spellCheck={false}
              className="block w-full resize-y bg-white px-2 py-1 font-mono text-xs focus:outline-none"
            />
          </div>
        </Field>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => mutation.mutate()}
            disabled={
              mutation.isPending
              || uploadMutation.isPending
              || !xml.trim()
              || !xmlValidation.ok
            }
            className="inline-flex items-center gap-1 rounded-md bg-bridge-primary px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-bridge-primaryDark disabled:opacity-40"
            title={
              xmlValidation.ok
                ? "Parse the XML into the IR"
                : "XML has errors — see the badge above the editor"
            }
          >
            {mutation.isPending
              ? <Loader2 size={12} className="animate-spin" />
              : <FileCode2 size={12} />}
            Parse
          </button>

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={mutation.isPending || uploadMutation.isPending}
            className="inline-flex items-center gap-1 rounded-md border border-bridge-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-bridge-ink hover:bg-bridge-mist disabled:opacity-40"
          >
            {uploadMutation.isPending
              ? <Loader2 size={12} className="animate-spin" />
              : <Upload size={12} />}
            Upload .xml
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xml,application/xml,text/xml"
            onChange={onFilePicked}
            className="hidden"
          />

          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="inline-flex items-center gap-1 rounded-md border border-bridge-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-bridge-ink hover:bg-bridge-mist"
            title="Browse the Cognos Content Store and use a report's search-path as the asset id"
          >
            <FolderOpen size={12} />
            Browse Cognos
          </button>

          <button
            type="button"
            onClick={() => {
              setLoadFeedback(null);
              loadSpec.mutate(assetId);
            }}
            disabled={!canFetchFromCognos || loadSpec.isPending}
            className="inline-flex items-center gap-1 rounded-md border border-bridge-primary/40 bg-bridge-primary/5 px-3 py-1.5 text-xs font-medium text-bridge-primary hover:bg-bridge-primary/15 disabled:cursor-not-allowed disabled:opacity-40"
            title={
              canFetchFromCognos
                ? "Fetch the report XML from Cognos via SOAP — populates the textarea below."
                : "Paste a Cognos search-path (starts with '/') as the asset id to enable."
            }
          >
            {loadSpec.isPending
              ? <Loader2 size={12} className="animate-spin" />
              : <CloudDownload size={12} />}
            Load XML from Cognos
          </button>

          {(mutation.isSuccess || uploadMutation.isSuccess) && (
            <button
              onClick={() => navigate("/translate")}
              className="inline-flex items-center gap-1 rounded-md border border-bridge-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-bridge-ink hover:bg-bridge-mist"
            >
              Continue to translate <ArrowRight size={12} />
            </button>
          )}
        </div>
        {loadFeedback && (
          <p
            className={`text-2xs ${
              loadSpec.data?.found
                ? "inline-flex items-center gap-1 text-bridge-success"
                : "text-bridge-slate"
            }`}
            role="status"
          >
            {loadSpec.data?.found && <CheckCircle2 size={11} aria-hidden />}
            {loadFeedback}
          </p>
        )}
      </section>

      {(mutation.error || uploadMutation.error) && (
        <p className="mt-4 rounded-md border border-bridge-danger/30 bg-bridge-danger/5 p-3 text-sm text-bridge-danger">
          Parse failed — {(mutation.error ?? uploadMutation.error)?.message}
        </p>
      )}

      {(mutation.data ?? uploadMutation.data) && (
        <IrPreview ir={(mutation.data ?? uploadMutation.data) as CognosReportIR} />
      )}

      <ContentPicker
        open={pickerOpen}
        onCancel={() => setPickerOpen(false)}
        onConfirm={onPickerConfirm}
      />
    </div>
  );
}

function IrPreview({ ir }: { ir: CognosReportIR }) {
  return (
    <section data-tour-id="parse-preview" className="mt-4 rounded-xl2 border border-bridge-slate/20 bg-white p-4 shadow-card">
      <h2 className="font-display text-base font-semibold text-bridge-ink">
        IR — {ir.displayName}
      </h2>
      <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-bridge-slate">
        <div>
          <div className="text-2xs uppercase tracking-wide">fields</div>
          <div className="font-mono text-base text-bridge-ink">{ir.fields.length}</div>
        </div>
        <div>
          <div className="text-2xs uppercase tracking-wide">filters</div>
          <div className="font-mono text-base text-bridge-ink">{ir.filters.length}</div>
        </div>
        <div>
          <div className="text-2xs uppercase tracking-wide">visualizations</div>
          <div className="font-mono text-base text-bridge-ink">{ir.visualizations.length}</div>
        </div>
      </div>
      {ir.packagePath && (
        <p className="mt-2 text-2xs text-bridge-slate">
          Package: <span className="font-mono">{ir.packagePath}</span>
        </p>
      )}
      {ir.fields.length > 0 && (
        <div className="mt-3">
          <div className="text-2xs font-medium uppercase tracking-wide text-bridge-slate">
            Fields
          </div>
          <ul className="mt-1 grid grid-cols-2 gap-1 text-xs">
            {ir.fields.map((f) => (
              <li key={f.name} className="flex items-center gap-1.5">
                <span className="font-mono text-bridge-ink">{f.name}</span>
                <span className="text-2xs text-bridge-slate">{f.dataType}</span>
                {f.expression && (
                  <span className="text-2xs text-bridge-accent">calc</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function XmlStatusBadge({
  validation,
}: {
  validation: ReturnType<typeof validateXml>;
}) {
  if (validation.ok) {
    const ns = validation.namespace ? ` · ${truncateNs(validation.namespace)}` : "";
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-bridge-success/10 px-2 py-0.5 text-2xs font-medium text-bridge-success"
        title={
          validation.namespace
            ? `Root <${validation.rootName}> in namespace ${validation.namespace}`
            : `Root <${validation.rootName}>`
        }
      >
        <CheckCircle2 size={11} aria-hidden />
        Valid · {validation.bytes.toLocaleString()} bytes ·{" "}
        {validation.elementCount.toLocaleString()} elements
        {ns}
      </span>
    );
  }
  const where =
    validation.line != null
      ? ` (line ${validation.line}${validation.column != null ? `, col ${validation.column}` : ""})`
      : "";
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-bridge-danger/10 px-2 py-0.5 text-2xs font-medium text-bridge-danger"
      title={validation.message}
    >
      <AlertTriangle size={11} aria-hidden />
      Invalid XML{where}
      <span className="hidden text-bridge-slate sm:inline">— {validation.message}</span>
    </span>
  );
}

/** Trim long {@code xmlns} URIs so the badge stays single-line. */
function truncateNs(ns: string): string {
  if (ns.length <= 36) return ns;
  return ns.replace(/^https?:\/\//, "").slice(0, 32) + "…";
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="text-2xs font-medium uppercase tracking-wide text-bridge-slate">
        {label}
      </span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}

const SAMPLE_XML = `<report name="Sales Variance Q3">
    <packageRef>/content/package[@name='Sales']</packageRef>
    <queryItem name="Region">
        <datatype>string</datatype>
    </queryItem>
    <queryItem name="Revenue">
        <datatype>decimal</datatype>
    </queryItem>
    <calculatedColumn name="Margin">
        <expression>[Revenue] - [Cost]</expression>
    </calculatedColumn>
    <columnChart refId="ch-1" name="Revenue by region">
        <dataItem refDataItem="Region"/>
        <dataItem refDataItem="Revenue"/>
    </columnChart>
</report>`;
